//region :: Imports ::
import {createRef, ReactElement, RefObject} from 'react';
import MComponent from '../../Component';
import {Dialog} from 'primereact/dialog';
import {DataTablePFSEvent} from 'primereact/datatable';
import {Column} from 'primereact/column';
import MTable, {MTableProps} from '../Table';
import MButton, {MButtonRTProps} from '../Button';
import {CloseIconName, OkIconName} from '../../../Icons';
import {
    getDynAccessToken,
    getDynEntityMetadata,
    _,
    getDynEntityFieldMetadata,
    MNullable,
    getDynPagedEntities,
    getDynNextPagedEntities,
    isNullOrUndefined,
    getDynEntity
} from '../../../Utils';
import {IMsalContext} from '@azure/msal-react';
import {evalulateBool} from '../Control';
import MTextBox, {MInputValueType} from '../TextBox';

//endregion

/** */
type Props = {
    environmentUrl: string;
    entityLogicalName: string;
    searchFieldName: string;
    searchTextMinLength?: number;
    searchDelay?: number;
    searchFilter?: string;
    tableFieldNames: string[];
    columnWidth?: number;
    authContext: IMsalContext;
    onClose: (id: string, name: string, entity: MNullable<any>) => void;
}

/** */
type State = {
    initializing: boolean;
    initialized: boolean;
    visible: boolean;
    entityMetadata: MNullable<any>;
    entityFieldMetadata: MNullable<any[]>;
    odataMaxPageSize: number;
    odataNextLink?: string;
    searchText: string;
    searching: boolean;
    table: MTableProps<any>;
    tableColumns: ReactElement<Column>[];
    selectedRow?: any;
    selectedEntity: MNullable<any>;
    selectButton: MButtonRTProps;
}

/** */
class MDynEntityLookupDialog extends MComponent<Props, State> {

    private m_initializing: boolean;
    private m_initialized: boolean;
    private m_timer: MNullable<NodeJS.Timeout>;
    private m_abortSignal: AbortController;
    private readonly m_searchTextRef: RefObject<MTextBox>;

    /** */
    public constructor(props: Props) {
        super(props);

        this.bindMethods();

        this.m_initializing = false;
        this.m_initialized = false;
        this.m_timer = null;
        this.m_abortSignal = new AbortController();
        this.m_searchTextRef = createRef<MTextBox>();

        this.initializeState();

    }

    /** */
    private bindMethods(): void {

        this.search = this.search.bind(this);
        this.isSearchTextBoxDisabled = this.isSearchTextBoxDisabled.bind(this);
        this.isTableDisabled = this.isTableDisabled.bind(this);
        this.isSelectButtonDisabled = this.isSelectButtonDisabled.bind(this);
        this.isCloseButtonDisabled = this.isCloseButtonDisabled.bind(this);
        this.getTableFieldNames = this.getTableFieldNames.bind(this);
        this.buildQuery = this.buildQuery.bind(this);
        this.buildSelectQuery = this.buildSelectQuery.bind(this);
        this.buildFilterQuery = this.buildFilterQuery.bind(this);
        this.dialog_onHide = this.dialog_onHide.bind(this);
        this.searchTextBox_onChange = this.searchTextBox_onChange.bind(this);
        this.table_onSelectionChange = this.table_onSelectionChange.bind(this);
        this.table_onPageChange = this.table_onPageChange.bind(this);
        this.selectButton_onClick = this.selectButton_onClick.bind(this);
        this.closeButton_onClick = this.closeButton_onClick.bind(this);
        this.show = this.show.bind(this);

    }

    /** */
    private initializeState(): void {

        const table: MTableProps<any> = {
            rowData: [],
            totalRows: 0,
            noRowsMessage: <span>No entities found. Please contact the <strong>Data Owner</strong> to add the entity you require.</span>,
            disabled: this.isTableDisabled
        };

        this.state = {
            initializing: false,
            initialized: false,
            visible: false,
            entityMetadata: null,
            entityFieldMetadata: null,
            odataMaxPageSize: 15,
            odataNextLink: undefined,
            searchText: '',
            searching: false,
            table: table,
            tableColumns: [],
            selectedRow: undefined,
            selectedEntity: null,
            selectButton: {}
        };

    }

    /** */
    private async search(searchText: string): Promise<void> {

        let rowData = [] as any[];
        let totalRows = 0;
        let nextLink = undefined as string | undefined;

        this.safeSetState(
            (draft: State) => draft.searching = true);

        try {

            const accessToken = await getDynAccessToken(this.props.authContext, this.props.environmentUrl);
            const selectQuery = this.buildSelectQuery(this.state.entityMetadata.PrimaryIdAttribute);
            let query = null as MNullable<string>;

            if(searchText.length === 0) {
                query = this.buildQuery(selectQuery, null);
            } else if(searchText.length >= this.props.searchTextMinLength!) {

                const filterQuery = this.buildFilterQuery(`contains(${this.props.searchFieldName},'${searchText}')`);
                query = this.buildQuery(selectQuery, filterQuery);

            }

            if(query === null) {
                return;
            }

            const entities = await getDynPagedEntities(
                this.props.environmentUrl,
                this.state.entityMetadata.EntitySetName,
                query,
                this.state.odataMaxPageSize,
                accessToken,
                this.m_abortSignal.signal);

            rowData = entities.value;
            totalRows = entities.count;
            nextLink = entities.nextLink;

        } finally {

            this.m_searchTextRef.current!.setFocus();
            this.safeSetState((draft: State) => {

                draft.searching = false;
                draft.table.rowData = rowData;
                draft.table.totalRows = totalRows;
                draft.odataNextLink = nextLink;

            });

        }

    }

    /** */
    private isSearchTextBoxDisabled(): boolean {

        return evalulateBool(this.state.selectButton.isBusy)
            || this.state.table.loading
            || this.state.searching
            || this.state.initializing;

    }

    /** */
    private isTableDisabled(): boolean {

        return evalulateBool(this.state.selectButton.isBusy)
            || this.state.searching
            || this.state.initializing;

    }

    /** */
    private isSelectButtonDisabled(): boolean {

        return isNullOrUndefined(this.state.selectedRow)
            || this.state.table.loading
            || this.state.searching
            || this.state.initializing;

    }

    /** */
    private isCloseButtonDisabled(): boolean {

        return evalulateBool(this.state.selectButton.isBusy)
            || this.state.table.loading
            || this.state.searching
            || this.state.initializing;

    }

    /** */
    private getTableFieldNames(): string[] {

        const comparer = (fieldName: string) => fieldName.toLowerCase() === this.props.searchFieldName.toLowerCase();
        return _.some(this.props.tableFieldNames, comparer) ? this.props.tableFieldNames : [this.props.searchFieldName, ...this.props.tableFieldNames];

    }

    /** */
    private buildQuery(selectQuery: string, filterQuery: MNullable<string>): string {
        return filterQuery === null || filterQuery.length === 0 ? selectQuery : `${selectQuery}&${filterQuery}`;
    }

    /** */
    private buildSelectQuery(primaryIdFieldName: string): string {

        const tableFieldNames = this.getTableFieldNames();
        const comparer = (fieldName: string) => fieldName.toLowerCase() === primaryIdFieldName.toLowerCase();
        const selectFieldNames = _.some(tableFieldNames, comparer) ? tableFieldNames : [primaryIdFieldName, ...tableFieldNames];

        return `$select=${_.join(selectFieldNames, ',')}`;

    }

    /** */
    private buildFilterQuery(andFilter: MNullable<string>): string {

        const hasSearchFilter = this.props.searchFilter !== undefined && this.props.searchFilter.length > 0;
        const hasAndFilter = andFilter !== null && andFilter.length > 0;

        if(!hasSearchFilter && !hasAndFilter) {
            return '';
        }

        let query = `$filter=`;

        if(hasSearchFilter) {
            query = `${query}${this.props.searchFilter}`;
        }

        if(hasAndFilter) {
            query = `${query} and ${andFilter}`;
        }

        return query;

    }

    /** */
    private dialog_onHide(): void {

        this.m_abortSignal.abort();
        this.safeSetState((draft: State) => draft.visible = false);

    }

    /** */
    private async searchTextBox_onChange(value: MInputValueType): Promise<void> {

        const searchText = value as string;

        if(this.m_timer !== null) {
            clearTimeout(this.m_timer);
        }

        this.m_timer = setTimeout(
            async() => await this.search(searchText),
            this.props.searchDelay);

        this.safeSetState(
            (draft: State) => draft.searchText = searchText);

    }

    /** */
    private async table_onSelectionChange(selection: any | any[] | undefined): Promise<void> {

        this.safeSetState((draft: State) =>
            draft.selectedRow = selection as any | undefined);

    }

    /** */
    private async table_onPageChange(event: DataTablePFSEvent): Promise<void> {

        if(this.state.odataNextLink === undefined) {
            return;
        }

        if(this.state.table.rowData.length >= event.first + (event.rows - 1)) {
            return;
        }

        this.safeSetState(
            (draft: State) => draft.table.loading = true);

        const rowData = this.state.table.rowData;
        const accessToken = await getDynAccessToken(this.props.authContext, this.props.environmentUrl);
        const entities = await getDynNextPagedEntities(this.state.odataNextLink!, event.rows, accessToken, this.m_abortSignal.signal);

        this.safeSetState((draft: State) => {

            draft.table.loading = false;
            draft.table.rowData = [...rowData, ...entities.value];
            draft.odataNextLink = entities.nextLink;

        });

    }

    /** */
    private async selectButton_onClick(): Promise<void> {

        this.safeSetState(
            (draft: State) => draft.selectButton.isBusy = true);

        try {

            const accessToken = await getDynAccessToken(this.props.authContext, this.props.environmentUrl);
            const id = _.get(this.state.selectedRow, this.state.entityMetadata.PrimaryIdAttribute);
            const name = _.get(this.state.selectedRow, this.state.entityMetadata.PrimaryNameAttribute);

            const entity = await getDynEntity(
                this.props.environmentUrl,
                this.state.entityMetadata.EntitySetName,
                id,
                accessToken,
                this.m_abortSignal.signal);

            this.props.onClose(id, name, entity);

        } finally {

            this.safeSetState((draft: State) => {

                draft.selectButton.isBusy = false;
                draft.visible = false;

            });

        }

    }

    /** */
    private closeButton_onClick(): void {

        this.m_abortSignal.abort();
        this.safeSetState((draft: State) => draft.visible = false);

    }

    /** */
    private getDialogFooterTemplate(): ReactElement {

        return (
            <div className="flex justify-content-end">
                <MButton label="Close"
                         icon={CloseIconName}
                         color="danger"
                         disabled={this.isCloseButtonDisabled}
                         onClick={this.closeButton_onClick}/>
                <MButton label="Select"
                         icon={OkIconName}
                         color="success"
                         isBusy={this.state.selectButton.isBusy}
                         disabled={this.isSelectButtonDisabled}
                         onClick={this.selectButton_onClick}/>
            </div>
        );

    }

    /** */
    private async initialize(): Promise<void> {

        if(this.m_initializing || this.m_initialized) {
            return Promise.resolve();
        }

        this.m_initializing = true;
        this.m_initialized = false;

        this.safeSetState((draft: State) => {
            draft.initializing = this.m_initializing;
            draft.initialized = this.m_initialized;
        });

        try {

            const tableFieldNames = this.getTableFieldNames();
            const accessToken = await getDynAccessToken(this.props.authContext, this.props.environmentUrl);

            //  Entity Metadata
            const entityMetadata = await getDynEntityMetadata(
                this.props.environmentUrl, this.props.entityLogicalName, accessToken, this.m_abortSignal.signal);

            //  Entity Field Metadata
            const entityFieldMetadata = await getDynEntityFieldMetadata(
                this.props.environmentUrl, this.props.entityLogicalName, tableFieldNames, accessToken, this.m_abortSignal.signal);

            //  Table Columns
            const tableColumns = entityFieldMetadata.map((fieldMetadata: any, index: number) => {

                const isLastItem = index === entityFieldMetadata.length - 1;
                const width = isLastItem ? '' : `${this.props.columnWidth}%`;
                const logicalName = fieldMetadata.LogicalName;
                const displayName = fieldMetadata.Labels.DisplayName;

                return (
                    <Column key={logicalName} field={logicalName} header={displayName} style={{width: width}}/>
                );

            });

            this.safeSetState((draft: State) => {

                draft.entityMetadata = entityMetadata;
                draft.entityFieldMetadata = entityFieldMetadata;
                draft.tableColumns = tableColumns;

            });

        } finally {

            this.m_initializing = false;
            this.m_initialized = true;

            this.safeSetState((draft: State) => {

                draft.initializing = this.m_initializing;
                draft.initialized = this.m_initialized;

            });

        }

    }

    /** */
    public async show(): Promise<void> {

        this.m_abortSignal = new AbortController();

        const accessToken = await getDynAccessToken(this.props.authContext, this.props.environmentUrl);
        const selectQuery = this.buildSelectQuery(this.state.entityMetadata.PrimaryIdAttribute);
        const filterQuery = this.buildFilterQuery(null);
        const query = this.buildQuery(selectQuery, filterQuery);

        const entities = await getDynPagedEntities(
            this.props.environmentUrl,
            this.state.entityMetadata.EntitySetName,
            query,
            this.state.odataMaxPageSize,
            accessToken,
            this.m_abortSignal.signal);

        this.safeSetState((draft: State) => {

            draft.visible = true;
            draft.table.rowData = entities.value;
            draft.table.totalRows = entities.count;
            draft.selectedRow = null;
            draft.selectedEntity = null;
            draft.odataNextLink = entities.nextLink;

        });

    }

    /** */
    public async componentDidMount(): Promise<void> {
        await this.initialize();
    }

    /** */
    public render(): ReactElement {

        const entityDisplayName = this.state.entityMetadata?.Labels.DisplayName;
        const placeholderText = `Type ${this.props.searchTextMinLength} or more characters to filter results...`;
        const style = {width: '50vw', height: '90vh'};

        return (
            <Dialog header={`Search for ${entityDisplayName}`}
                    footer={this.getDialogFooterTemplate()}
                    visible={this.state.visible}
                    position="center"
                    modal={true}
                    closable={true}
                    closeOnEscape={true}
                    draggable={false}
                    resizable={false}
                    maximizable={false}
                    style={style}
                    onHide={this.dialog_onHide}>

                <MTextBox id="searchText"
                          type="text"
                          placeholderText={placeholderText}
                          maxLength={100}
                          disabled={this.isSearchTextBoxDisabled}
                          className="mb-3 w-full"
                          onChange={this.searchTextBox_onChange}
                          ref={this.m_searchTextRef}/>

                <MTable rowKey={this.state.entityMetadata?.PrimaryIdAttribute}
                        rowData={this.state.table.rowData}
                        rowsPerPage={this.state.odataMaxPageSize}
                        lazy={true}
                        totalRows={this.state.table.totalRows}
                        loading={this.state.table.loading}
                        noRowsMessage={this.state.table.noRowsMessage}
                        showRowsPerPageDropdown={false}
                        showPageLinks={false}
                        disabled={this.state.table.disabled}
                        onSelectionChange={this.table_onSelectionChange}
                        onPageChange={this.table_onPageChange}>

                    {this.state.tableColumns}

                </MTable>

            </Dialog>

        );

    }

}

/** */
// @ts-ignore
MDynEntityLookupDialog.defaultProps = {
    searchTextMinLength: 2,
    searchDelay: 250,
    columnWidth: 25
};

export default MDynEntityLookupDialog;
